import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

interface PieChartProps {
  labels: string[];
  series: number[];
  width?: number;
  colors?: string[];
  title?: string;
}

const DEFAULT_COLORS = ['#2563EB', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];

/* Donut chart used across dashboard/assessment pages — takes labels + a parallel series of values */
const PieChart: React.FC<PieChartProps> = ({ labels, series, width, colors, title }) => {
  const total = series.reduce((a, b) => a + b, 0);
  const palette = colors && colors.length ? colors : DEFAULT_COLORS;

  const option: EChartsOption = {
    color: palette,
    tooltip: {
      trigger: 'item',
      formatter: (params: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const p = params as any;
        return `${p.marker}${p.name}: <strong>${p.value}</strong> (${p.percent}%)`;
      },
    },
    legend: {
      bottom: 0,
      left: 'center',
      textStyle: { fontSize: 12, color: '#374151' },
      itemWidth: 10,
      itemHeight: 10,
      icon: 'circle',
    },
    series: [
      {
        type: 'pie',
        radius: ['55%', '75%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: true,
        label: {
          formatter: (params: unknown) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const p = params as any;
            return total > 0 ? `${Math.floor((p.value / total) * 100)}%` : '0%';
          },
          fontSize: 11,
          color: '#374151',
        },
        labelLine: { length: 8, length2: 6 },
        data: labels.map((name, i) => ({ name, value: Number(series[i]) || 0 })),
      },
    ],
    graphic: {
      elements: [
        {
          type: 'text',
          left: 'center',
          top: '40%',
          style: {
            text: String(total),
            fontSize: 20,
            fontWeight: 700,
            fill: '#111827',
          },
        },
        {
          type: 'text',
          left: 'center',
          top: '48%',
          style: {
            text: 'Total',
            fontSize: 11,
            fill: '#6b7280',
          },
        },
      ],
    },
  };

  return (
    <div className='w-full bg-white rounded-lg p-4'>
      {title && <h3 className='text-lg font-semibold text-gray-900 mb-4'>{title}</h3>}
      <div className='flex justify-center'>
        <ReactECharts option={option} style={{ height: 340, width: width ?? 360 }} opts={{ renderer: 'svg' }} />
      </div>
    </div>
  );
};

export default PieChart;
